import { MergeFlow } from "./MergeFlow";
import { MergeFooter } from "./MergeFooter";
import { ModalAppearanceProps } from "../modalProps";
import { VyneModal } from "@vyne-shared/components/vyneModal/VyneModal";
import { antdModalV5 } from "@ebay/nice-modal-react";
import { captureException } from "@sentry/react";
import { isAxiosApiProblemWithDetail } from "@vyne-shared/api/errors";
import { useMergePatient } from "api/clients/useMergePatient";
import { useReducer, useState } from "react";
import { useSearchFlowContext } from "../SearchFlow/useSearchFlow";
import type { ExistingPatientResult } from "api/responses";
import type { MergeAction, MergeState } from "./mergeFlow.props";

const defaultMergeState = Object.freeze({
    pmsId: undefined,
    confirmedInitial: false,
    confirmedFinal: false,
    mergedPrp: undefined,
} satisfies MergeState);

function reducer(priorState: MergeState, action: MergeAction): MergeState {
    switch (action.type) {
        case "pmspatient":
            return {
                pmsId: action.pmsId,
                confirmedInitial: false,
                confirmedFinal: false,
                mergedPrp: undefined,
            };
        case "merge":
            return { ...priorState, confirmedInitial: true, confirmedFinal: priorState.confirmedInitial };
        case "merged":
            return { ...priorState, mergedPrp: action.mergedPrp };
        default:
            throw new Error("MergeForm reducer incorrect action"); // this can't happen
    }
}

/**
 * Provides Merge Flow, that can be launched from the context of a New Payment Request.
 *
 * "ModalContent" convention indicates we don't call useModal here; rather, 'modal' is provided as a prop. The
 * "ModalContent" approach allows NewPaymentModal to own the modal life cycle, but with the visible contents (antd Modal,
 * including its title, body, and footer) able to be swapped between SearchFlowModalContent and MergeFlowModalContent.
 */
export const MergeFlowModalContent = () => {
    const [mergeState, dispatchMergeAction] = useReducer(reducer, defaultMergeState);
    const { merging, modal, selectedTransactor: unlinkedPrp, onSelectExistingPrp } = useSearchFlowContext();

    if (!merging) {
        throw new Error("MergeFlowModalContent should only be used in a merging state");
    }

    const { isPending, nextStep, mergeFailedMessage } = useMergePatientStep(
        unlinkedPrp.id,
        mergeState,
        dispatchMergeAction,
        onSelectExistingPrp,
    );

    const footer =
        mergeState.pmsId && !mergeState.confirmedFinal ? (
            <MergeFooter
                onCancel={() => void modal.hide()}
                onMerge={nextStep}
                confirmation={mergeState.confirmedInitial}
                loading={isPending}
            />
        ) : null;

    return (
        <VyneModal {...antdModalV5(modal)} {...ModalAppearanceProps} title="Patient Merge" footer={footer}>
            <MergeFlow
                mergeState={mergeState}
                dispatchMergeAction={dispatchMergeAction}
                unlinkedPrp={unlinkedPrp}
                mergeFailedMessage={mergeFailedMessage}
            />
        </VyneModal>
    );
};

// handle moving to different steps in the merge flow, return error message if merge fails
const useMergePatientStep = (
    patientId: number,
    state: MergeState,
    dispatch: React.Dispatch<MergeAction>,
    onSelectExistingPrp: (prp: ExistingPatientResult) => void,
) => {
    const [mergeFailedMessage, setMergeFailedMessage] = useState("");

    const { isPending, mutate } = useMergePatient({
        onError: (error) => {
            captureException(new Error("Merge operation failed", { cause: error }));

            const message = isAxiosApiProblemWithDetail(error) ? error.response.data.detail : "Merge Failed";
            setMergeFailedMessage(message);
        },
        onSuccess: (result) => {
            // ensure there's no failure state just in case
            setMergeFailedMessage("");

            // this steps us to the last state
            dispatch({ type: "merged", mergedPrp: result });

            // upon completion of the merge, transition to regular Payment Request flow, targeting the mergedPrp
            onSelectExistingPrp(result);
        },
    });

    // function that moves to the next step of merging
    const nextStep = () => {
        // check if we only need final confirmation left
        if (state.pmsId && state.confirmedInitial && !state.confirmedFinal) {
            // this step *is* the final confirmation, so we can go ahead and submit
            mutate({ unlinkedPatientId: patientId, pmsId: state.pmsId });
        }

        // we keep dispatching the same action to continue
        dispatch({ type: "merge" });
    };

    return { isPending, nextStep, mergeFailedMessage };
};
